import { Button, Result } from 'antd';

export interface AccessDeniedProps {
  /** Custom title for the access denied message */
  title?: string;
  /** Custom message explaining why access is denied */
  message?: string;
  /** Whether to show role information */
  showRoleInfo?: boolean;
  /** Required roles that the user doesn't have */
  requiredRoles?: string[];
  /** User's current roles */
  currentRoles?: string[];
  /** Callback when user clicks go back button */
  onGoBack?: () => void;
  /** Callback when user clicks logout button */
  onLogout?: () => void;
  /** Custom back button text */
  backText?: string;
  /** Custom logout button text */
  logoutText?: string;
}

/**
 * Access Denied Component
 *
 * Displays a friendly message when a user doesn't have the required roles
 * to access a protected route. Provides options to go back or logout.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AccessDenied
 *   onGoBack={() => router.back()}
 *   onLogout={() => logout()}
 * />
 *
 * // With role information (for debugging)
 * <AccessDenied
 *   showRoleInfo
 *   requiredRoles={['admin']}
 *   currentRoles={['user']}
 *   onGoBack={() => router.back()}
 *   onLogout={() => logout()}
 * />
 * ```
 */
export function AccessDenied({
  title = 'Access Denied',
  message = 'You do not have permission to access this page.',
  showRoleInfo = false,
  requiredRoles,
  currentRoles,
  onGoBack,
  onLogout,
  backText = 'Go Back',
  logoutText = 'Logout',
}: AccessDeniedProps) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <Result
        status="403"
        title={title}
        subTitle={
          <div className="space-y-2">
            <p>{message}</p>
            {showRoleInfo && requiredRoles && requiredRoles.length > 0 && (
              <p className="text-sm text-gray-500">
                Required role: {requiredRoles.join(' or ')}
              </p>
            )}
            {showRoleInfo && currentRoles && currentRoles.length > 0 && (
              <p className="text-sm text-gray-500">
                Your roles: {currentRoles.join(', ')}
              </p>
            )}
          </div>
        }
        extra={
          <div className="flex gap-2 justify-center">
            {onGoBack && <Button onClick={onGoBack}>{backText}</Button>}
            {onLogout && (
              <Button type="primary" danger onClick={onLogout}>
                {logoutText}
              </Button>
            )}
          </div>
        }
      />
    </div>
  );
}
